import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";

import {
  CheckCircle,
  Close as CloseIcon,
  Event,
  Person,
  RadioButtonUnchecked
} from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { collection, doc, getDoc, getDocs, query, updateDoc } from "firebase/firestore";

import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";

const Todo = () => {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    if (!currentUser?.uid) {
      enqueueSnackbar("Utilisateur non connecté", { variant: "warning" });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const ordersQuery = query(collection(db, `users/${currentUser.uid}/orders`));
      const customersQuery = query(collection(db, `users/${currentUser.uid}/customers`));
      const ordersSnapshot = await getDocs(ordersQuery);
      const customersSnapshot = await getDocs(customersQuery);

      const customersById = customersSnapshot.docs.reduce((acc, customerDoc) => {
        const customer = customerDoc.data();
        const fullName = `${customer?.firstname || ""} ${customer?.lastname || ""}`.trim();
        acc[customerDoc.id] = fullName || customer?.company || customer?.email || "Client inconnu";
        return acc;
      }, {});

      const allTasks = [];
      ordersSnapshot.docs.forEach(orderDoc => {
        const orderData = orderDoc.data();
        const orderId = orderDoc.id;
        const workflowTasks = orderData?.workflow?.tasks;
        const customerNameFromOrderId = orderData?.client_id
          ? customersById[orderData.client_id]
          : null;
        const customerName = customerNameFromOrderId ||
          orderData.customerName ||
          orderData.clientName ||
          orderData.client ||
          "Client inconnu";

        if (!Array.isArray(workflowTasks)) {
          return;
        }

        workflowTasks.forEach((task, taskIndex) => {
          const isCompleted = Boolean(task?.done ?? task?.completed);
          allTasks.push({
            id: `${orderId}_${task?.id || taskIndex}`,
            orderId,
            taskIndex,
            taskName: task?.label || task?.name || task?.title || "Tâche sans nom",
            completed: isCompleted,
            customerName,
            eventDate:
              orderData.selectedDate ||
              orderData.eventDate ||
              orderData.date ||
              "Date non définie"
          });
        });
      });

      setTasks(allTasks);
    } catch (error) {
      enqueueSnackbar(`Erreur: ${error.message || "Problème de chargement"}`, {
        variant: "error"
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser, enqueueSnackbar]);

  useEffect(() => {
    if (currentUser) {
      fetchTasks();
    }
  }, [currentUser, fetchTasks]);

  const toggleTaskCompletion = async task => {
    try {
      const orderDocRef = doc(db, `users/${currentUser.uid}/orders/${task.orderId}`);
      const orderDocSnapshot = await getDoc(orderDocRef);
      const orderData = orderDocSnapshot.data();
      const workflowTasks = orderData?.workflow?.tasks;

      if (!Array.isArray(workflowTasks) || !workflowTasks[task.taskIndex]) {
        throw new Error("Tâche introuvable");
      }

      const nextCompleted = !task.completed;
      const updatedTasks = [...workflowTasks];
      updatedTasks[task.taskIndex] = {
        ...updatedTasks[task.taskIndex],
        done: nextCompleted,
        completed: nextCompleted,
        dateDone: nextCompleted ? new Date().toISOString() : null
      };

      await updateDoc(orderDocRef, {
        "workflow.tasks": updatedTasks,
        updatedAt: new Date().toISOString()
      });

      setTasks(prevTasks =>
        prevTasks.map(t =>
          t.id === task.id ? { ...t, completed: nextCompleted } : t
        )
      );

      enqueueSnackbar(
        `Tâche ${nextCompleted ? "marquée comme terminée" : "marquée comme en cours"}`,
        { variant: "success" }
      );
    } catch (error) {
      enqueueSnackbar("Erreur lors de la mise à jour de la tâche", {
        variant: "error"
      });
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === "completed") return task.completed;
    if (filter === "pending") return !task.completed;
    return true;
  });

  const formatDate = dateString => {
    if (!dateString) return "Date non définie";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR");
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: "100%", mb: 4 }}>
        <Typography>Chargement des tâches...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "100%", mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          background: theme =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(148, 163, 184, 0.14), rgba(71, 85, 105, 0.16))"
              : "#ffffff"
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#0f172a",
                fontWeight: 800
              }}
            >
              Liste des tâches
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredTasks.length} / {tasks.length} tâche(s)
            </Typography>
          </Box>
          {filter !== "all" && (
            <Chip
              label={`Filtre actif: ${filter === "pending" ? "en cours" : "terminées"}`}
              variant="outlined"
              onDelete={() => setFilter("all")}
              deleteIcon={<CloseIcon />}
              sx={{
                backgroundColor: theme =>
                  theme.palette.mode === "dark"
                    ? "rgba(148, 163, 184, 0.12)"
                    : "rgba(15, 23, 42, 0.04)",
                borderColor: theme => theme.palette.divider
              }}
            />
          )}
        </Stack>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filtrer les tâches</InputLabel>
          <Select
            value={filter}
            label="Filtrer les tâches"
            onChange={e => setFilter(e.target.value)}
          >
            <MenuItem value="all">Toutes les tâches</MenuItem>
            <MenuItem value="pending">Tâches en cours</MenuItem>
            <MenuItem value="completed">Tâches terminées</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: theme => theme.palette.mode === "dark"
            ? "0 4px 6px rgba(0, 0, 0, 0.4)"
            : "0 1px 2px rgba(15, 23, 42, 0.06)",
          overflow: "hidden",
          backgroundColor: theme => theme.palette.mode === "dark"
            ? "background.paper"
            : "#fff"
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme => theme.palette.mode === "dark"
                  ? "rgba(148, 163, 184, 0.08)"
                  : "#f8fafc"
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b"
                }}
              >
                Statut
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b"
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Person fontSize="small" />
                  Client
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b"
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Event fontSize="small" />
                  Date de l&apos;événement
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b"
                }}
              >
                Tâche
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b"
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary">
                    Aucune tâche trouvée
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTasks.map(task => (
                <TableRow
                  key={task.id}
                  sx={{
                    transition: "background-color 0.2s",
                    "&:hover": {
                      backgroundColor: theme => theme.palette.mode === "dark"
                        ? "rgba(148, 163, 184, 0.12)"
                        : "#f8fafc"
                    }
                  }}
                >
                  <TableCell>
                    <Chip
                      icon={task.completed ? <CheckCircle /> : <RadioButtonUnchecked />}
                      label={task.completed ? "Terminée" : "En cours"}
                      color={task.completed ? "success" : "warning"}
                      variant={task.completed ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>{task.customerName}</TableCell>
                  <TableCell>{formatDate(task.eventDate)}</TableCell>
                  <TableCell>{task.taskName}</TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        task.completed
                          ? "Marquer comme en cours"
                          : "Marquer comme terminée"
                      }
                    >
                      <IconButton
                        onClick={() => toggleTaskCompletion(task)}
                        sx={{
                          color: "#b98908",
                          "&:hover": {
                            backgroundColor: "rgba(212, 164, 17, 0.12)"
                          }
                        }}
                      >
                        {task.completed ? (
                          <RadioButtonUnchecked />
                        ) : (
                          <CheckCircle />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Total: {filteredTasks.length} tâche(s) • En cours:{" "}
          {tasks.filter(t => !t.completed).length} • Terminées:{" "}
          {tasks.filter(t => t.completed).length}
        </Typography>
      </Box>
    </Box>
  );
};

export default Todo;
